import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';

import { getTracerModules } from '@apptracer/sdk/lite';
import { nanoid } from 'nanoid';

import { useWidget } from '../context/WidgetContext';
import type {
  HTMLInputEvent,
  LimitType,
  UploadEntry,
  UploadFiles,
  UploadResult,
  UseUploadFilesReturn,
} from '../types/files';
import { FILE_UPLOAD_STATUS } from '../types/files';
import { isRateLimitError } from '../types/guards';
import { getFileFormatData } from '../utils';

import { packageName } from '../../config';
import { uploadFileApi } from '../api/uploadFileApi';

interface UseUploadParams {
  /** Максимальное количество файлов (по умолчанию без ограничений) */
  maxFiles?: number;
  /** Максимальный размер файла в мегабайтах (по умолчанию без ограничений) */
  maxFileSizeMB?: number;
  /** Callback при превышении лимитов */
  onLimitExceeded?: (type: LimitType) => void;
}

interface UploadState {
  selectedFiles: File[];
  uploadedResult: UploadResult[];
  isUpLoading: boolean;
  fileLoadingStatus: Record<string, boolean>;
}

const uploadFolder = nanoid();

/** Хук для управления загрузко файлов */
export const useUploadFiles = (props?: UseUploadParams): UseUploadFilesReturn => {
  const { maxFiles, maxFileSizeMB, onLimitExceeded } = props || {};
  const { setError, localeMessages } = useWidget();

  const tracerError = getTracerModules(packageName).error;

  const [shouldUpload, setShouldUpload] = useState(false);

  const [uploadState, setUploadState] = useState<UploadState>({
    selectedFiles: [],
    uploadedResult: [],
    isUpLoading: false,
    fileLoadingStatus: {},
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetUploader = useCallback(() => {
    setUploadState({
      selectedFiles: [],
      uploadedResult: [],
      isUpLoading: false,
      fileLoadingStatus: {},
    });
  }, []);

  const validateFiles = useCallback(
    (files: File[]) => {
      let validFiles = [...files];
      let countExceeded = false;
      let sizeExceeded = false;

      // Проверяет, превышает ли количество загружаемых файлов лимиты
      if (maxFiles && files.length > maxFiles) {
        countExceeded = true;
        validFiles = files.slice(0, maxFiles);
      }

      // Проверят, превышает ли размер загружаемого файла лимит
      if (maxFileSizeMB) {
        const maxSizeBytes = Math.round(maxFileSizeMB * 1024 * 1024);
        const hasOversizedFiles = validFiles.some((file) => file.size > maxSizeBytes);

        if (hasOversizedFiles) {
          sizeExceeded = true;
          validFiles = validFiles.filter((file) => file.size <= maxSizeBytes);
        }
      }

      // Определяет тип превышения лимита для вывода ошибки
      let exceededType: LimitType | undefined;
      if (countExceeded && sizeExceeded) {
        exceededType = 'all';
      } else if (countExceeded) {
        exceededType = 'count';
      } else if (sizeExceeded) {
        exceededType = 'size';
      }

      // Если любой из лимитов был превышен, нужно вызвать колбек и передать туда тип лимита
      if (exceededType && onLimitExceeded) {
        onLimitExceeded(exceededType);
      }

      return validFiles;
    },
    [maxFiles, maxFileSizeMB, onLimitExceeded]
  );

  // Обрабатывает выбранные файлы, фильтрует
  const handleSetSelectedFiles = useCallback(
    (files: File[]) => {
      const validatedFiles = validateFiles(files);

      setUploadState((prev) => {
        const uniqueFiles = validatedFiles.filter(
          (file) => !prev.selectedFiles.some((prevFile) => prevFile.name === file.name)
        );

        return { ...prev, selectedFiles: [...prev.selectedFiles, ...uniqueFiles] };
      });

      setShouldUpload(true);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [validateFiles]
  );

  // Получает выбранные в поле файлы
  const handleFilesChange = useCallback(
    (event: HTMLInputEvent) => {
      const files: File[] = event.target.files ? Array.from(event.target.files) : [];
      handleSetSelectedFiles(files);
    },
    [handleSetSelectedFiles]
  );

  const setSelectedFilesFromInput = useCallback(
    (elem: HTMLInputElement) => {
      const files: File[] = elem.files ? Array.from(elem.files) : [];
      handleSetSelectedFiles(files);
    },
    [handleSetSelectedFiles]
  );

  const uploadFiles = useCallback(async () => {
    const { selectedFiles, uploadedResult } = uploadState;

    if (selectedFiles.length === 0) {
      return;
    }

    const filesToUpload = selectedFiles.filter(
      (file) => !uploadedResult.some((result) => result.file.name === file.name && result.status === 'fulfilled')
    );

    if (filesToUpload.length === 0) {
      return;
    }

    // Меняем статус загрузки с началом загрузки
    setUploadState((prev) => ({ ...prev, isUpLoading: true }));

    try {
      const uploadResults: UploadResult[] = await Promise.all(
        filesToUpload.map(async (file) => {
          try {
            const data = await uploadFileApi(file, uploadFolder);

            const uploadEntry: UploadEntry = {
              name: file.name,
              status: FILE_UPLOAD_STATUS.Uploaded,
              uploaded: data,
              error: null,
            };

            return { status: 'fulfilled', value: uploadEntry, file };
          } catch (error) {
            if (isRateLimitError(error)) {
              setError?.(localeMessages.errors.rateLimit);

              tracerError?.error(localeMessages.errors.rateLimit, {
                keys: {
                  fileName: file.name,
                  fileSize: file.size,
                },
              });
            }
            return { status: 'rejected', reason: error, file };
          }
        })
      );

      setUploadState((prev) => {
        const resultsMap = new Map<string, UploadResult>();
        prev.uploadedResult.forEach((result) => resultsMap.set(result.file.name, result));
        uploadResults.forEach((result) => resultsMap.set(result.file.name, result));

        return {
          ...prev,
          uploadedResult: Array.from(resultsMap.values()),
          selectedFiles: prev.selectedFiles.filter(
            (file) => !uploadResults.some((result) => result.file.name === file.name && result.status === 'rejected')
          ),
        };
      });
    } finally {
      // Меняем статус загрузки
      setUploadState((prev) => ({ ...prev, isUpLoading: false }));
    }
  }, [uploadState, setError, localeMessages, tracerError]);

  useEffect(() => {
    if (shouldUpload && uploadState.selectedFiles.length > 0) {
      setShouldUpload(false);
      uploadFiles();
    }
  }, [shouldUpload, uploadState.selectedFiles, uploadFiles]);

  const uploadedFiles = useMemo(() => {
    const result: UploadFiles = { success: [], rejected: [] };
    if (uploadState.uploadedResult.length > 0) {
      result.success = uploadState.uploadedResult.filter(({ status }) => status === 'fulfilled');
      result.rejected = uploadState.uploadedResult.filter(({ status }) => status === 'rejected');
    }
    return result;
  }, [uploadState.uploadedResult]);

  const removeFile = useCallback((fileName: string) => {
    setUploadState((prev) => ({
      ...prev,
      selectedFiles: prev.selectedFiles.filter((file) => file.name !== fileName),
      uploadedResult: prev.uploadedResult.filter((result) => result.file.name !== fileName),
    }));
  }, []);

  const retryUploadFile = useCallback(
    async (fileName: string) => {
      const fileToRetry = uploadState.uploadedResult.find(
        (result) => result.file.name === fileName && result.status === 'rejected'
      );

      if (!fileToRetry) {
        return;
      }

      const file = fileToRetry.file;

      setUploadState((prev) => ({
        ...prev,
        fileLoadingStatus: { ...prev.fileLoadingStatus, [fileName]: true },
        isUpLoading: true,
      }));

      try {
        const data = await uploadFileApi(file, uploadFolder);

        const uploadEntry: UploadEntry = {
          name: file.name,
          status: FILE_UPLOAD_STATUS.Uploaded,
          uploaded: data,
          error: null,
        };

        setUploadState((prev) => ({
          ...prev,
          uploadedResult: prev.uploadedResult.map((result) =>
            result.file.name === file.name ? { status: 'fulfilled', value: uploadEntry, file } : result
          ),
        }));
      } catch (error) {
        if (isRateLimitError(error)) {
          setError?.(localeMessages.errors.rateLimit);

          tracerError?.error(localeMessages.errors.rateLimit, {
            keys: {
              fileName: file.name,
              fileSize: file.size,
            },
          });
        }
        setUploadState((prev) => ({
          ...prev,
          uploadedResult: prev.uploadedResult.map((result) =>
            result.file.name === file.name ? { status: 'rejected', reason: error, file } : result
          ),
        }));
      } finally {
        setUploadState((prev) => ({
          ...prev,
          fileLoadingStatus: { ...prev.fileLoadingStatus, [fileName]: false },
          isUpLoading: false,
        }));
      }
    },
    [uploadState.uploadedResult, setError, localeMessages, tracerError]
  );

  const isFileUploading = useCallback(
    (fileName: string) => {
      return uploadState.fileLoadingStatus[fileName] || false;
    },
    [uploadState.fileLoadingStatus]
  );

  const attachmentsData = useMemo(() => getFileFormatData(uploadedFiles), [uploadedFiles]);

  return {
    isUpLoading: uploadState.isUpLoading,
    fileInputRef,
    uploadedFiles,
    handleFilesChange,
    removeFile,
    resetUploader,
    retryUploadFile,
    isFileUploading,
    attachmentsData,
    handleSetSelectedFiles,
    setSelectedFilesFromInput,
  };
};
